import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/* The Xano signatures these stand in for were untyped, and the screens read
   fields off them freely. Narrowing here would only move the errors. */
/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Drop-in replacements for the Xano deal-quote calls, with the same signatures
 * and the same response shapes.
 *
 * The screens that use these were designed and built already; the migration is
 * meant to change where the numbers come from, not what they look like. So
 * rather than rewriting a 1,700-line panel, this maps the new API onto the
 * contract it already expects. It is a translation layer with a finite life:
 * when nothing imports it, the old response shape is gone for good.
 */

type Payload<T> = AxiosRequestConfig<T>;

export type GetCountryRequestType = Partial<{
  headers: { Authorization: string };
}>;

export type GetCountryResponseType = { country: string; currency: string };

export async function getCountry(
  _payload: Payload<unknown>,
): Promise<{ data: GetCountryResponseType[] }> {
  const { data } = await moneefloApi.get("/catalog/countries", {
    params: { withCurrency: true },
  });
  return {
    data: (data as { name: string; currency: string | null }[])
      .filter((row) => row.currency)
      .map((row) => ({ country: row.name, currency: row.currency as string })),
  };
}

export type GetPurposeRequestType = Partial<{
  headers: { Authorization: string };
  product_id: number;
}>;

export type GetPurposeResponseType = {
  name: string;
  id: number;
  /** Xano's column name, which the deal form matches on. */
  loan?: boolean;
  isLoan?: boolean;
  /**
   * Which wire service this purpose is for: Flywire, WesternUnion, CIBC,
   * PayMyTuition, ViaTransfer. "Education with loan" exists once per
   * service, so the form needs this to tell them apart.
   */
  mode?: string;
};

export async function getPurpose(payload: {
  params?: { product_id?: number };
}): Promise<{ data: GetPurposeResponseType[] }> {
  const productId = payload?.params?.product_id;
  const { data } = await moneefloApi.get("/catalog/purposes", {
    // product_id 0 meant "all" in Xano.
    params: productId ? { productId: String(productId) } : {},
  });
  return {
    data: (
      data as { id: string; name: string; isLoan?: boolean; mode?: string }[]
    ).map((purpose) => ({
      id: Number(purpose.id),
      name: purpose.name,
      // Both spellings: the deal form reads `loan`, as Xano returned it,
      // and other screens read `isLoan`.
      loan: purpose.isLoan,
      isLoan: purpose.isLoan,
      mode: purpose.mode,
    })),
  };
}

/** Paise as a string, into the plain rupee number the old screens render. */
const rupees = (minor: string | null | undefined): number =>
  Number(minor ?? 0) / 100;

type QuoteResponse = {
  currency: string;
  fxAmountMinor: string;
  customerRate: string;
  baseAmountMinor: string;
  lines: { type: string; amountMinor: string; taxMinor: string }[];
  totalPayableMinor: string;
  settlementRate?: string;
  baseCustomerRate: string;
  atALoss: boolean;
  l1NetMinor?: string;
  servicePartnerId?: string;
  ttChargeMinor: string;
  foreignBankChargeMinor: string;
  commissions: { partnerLevel: number; amountMinor: string }[];
};

export type PostDealCalcWithMarginRequestType = Record<string, unknown>;

/**
 * The old deal-calc, answered by POST /orders/quote.
 *
 * Xano returned per-level profit and cumulative commission so the screen could
 * work out what to show. Those come straight off the quote's commission rows
 * now, keyed by level, so the same fields are filled without the screen
 * needing to know how they were derived.
 */
export async function postDealCalcWithMargin(
  payload: Payload<PostDealCalcWithMarginRequestType>,
): Promise<{ data: any }> {
  const body = (payload.data ?? {}) as Record<string, string | number>;

  const { data } = await moneefloApi.post<QuoteResponse>("/orders/quote", {
    productId: undefined,
    purposeId: body.purpose_id ? String(body.purpose_id) : undefined,
    currency: body.currency,
    fxAmount: Number(body.amount),
    servicePartnerId: body.service_partner_id
      ? String(body.service_partner_id)
      : undefined,
    addOnRate: body.addon_rates ? Number(body.addon_rates) : undefined,
    // Who the deal is for. Without it the chain is priced as if it were
    // ours and the partner earns nothing, which is what used to force
    // their margin to be folded into our add-on.
    originOrgId: body.created_by ? String(body.created_by) : undefined,
    partnerMargin:
      body.partner_margin !== undefined && body.partner_margin !== ""
        ? Number(body.partner_margin)
        : undefined,
    discount: body.discount ? Number(body.discount) : undefined,
    // The operator's own figures, when the screen offers them. Xano's
    // deal-calc took these and so does the quote; they were being dropped
    // here, so typing a rate changed nothing. A customer rate is the exact
    // price promised, and the server works the adjustment out from it.
    customerRate: body.customer_rate ? Number(body.customer_rate) : undefined,
    settlementRate: body.settlement_rates
      ? Number(body.settlement_rates)
      : undefined,
    ttCharge: body.tt_charges !== undefined && body.tt_charges !== ""
      ? Number(body.tt_charges)
      : undefined,
    foreignBankCharge:
      body.foreign_bank_charges !== undefined && body.foreign_bank_charges !== ""
        ? Number(body.foreign_bank_charges)
        : undefined,
    fxCharges: body.fx_charges === "OUR" ? "OUR" : "BEN",
  });

  const line = (type: string) => data.lines.find((l) => l.type === type);
  const commissionAt = (level: number) =>
    rupees(
      data.commissions.find((c) => c.partnerLevel === level)?.amountMinor ??
        "0",
    );

  return {
    data: {
      base_amount: rupees(data.baseAmountMinor),
      payable_amount: rupees(data.totalPayableMinor),
      customer_rate: data.customerRate,
      fixed_customer_rate: data.customerRate,
      settlement_rates: data.settlementRate ?? "",
      // The standard rate for this corridor, before the operator moved it.
      // The screen used to reach this by adding up the settlement rate and
      // the commissions, which a partner may not see: they got NaN, and
      // setting a customer rate failed. Handed over directly instead.
      base_customer_rate: data.baseCustomerRate,
      // Selling below cost is allowed; the screen warns rather than blocks.
      // The amount is only there for someone who may see our margin.
      at_a_loss: data.atALoss,
      // What L1 is left with: its own commission plus whatever remains after
      // the provider and the rest of the chain. Negative is the loss.
      margin: data.l1NetMinor === undefined ? null : rupees(data.l1NetMinor),
      service_partner: data.servicePartnerId ?? "",
      // Nothing in the new model distinguishes an online partner from a manual
      // one yet, so the screen is told the honest thing rather than a guess.
      service_partner_mode: data.settlementRate ? "online" : "",
      gst: rupees(line("gst")?.amountMinor),
      tcs: rupees(line("tcs")?.amountMinor),
      tt_charges: rupees(data.ttChargeMinor),
      tt_charges_with_gst:
        rupees(line("tt_charge")?.amountMinor) +
        rupees(line("tt_charge")?.taxMinor),
      foreign_bank_charges: rupees(data.foreignBankChargeMinor),
      foreign_bank_charges_with_gst:
        rupees(line("bank_charge")?.amountMinor) +
        rupees(line("bank_charge")?.taxMinor),
      profit: {
        // What L1 actually makes, not what the rate card says it earns.
        // An operator who moves the customer rate moves L1's share and
        // nobody else's, so a raised rate showed a profit of 300 -- the
        // commission alone -- however far it was raised. L2 and L3 earn
        // what was agreed either way, so theirs is the commission.
        l1:
          data.l1NetMinor === undefined
            ? commissionAt(1)
            : rupees(data.l1NetMinor),
        l2: commissionAt(2),
        l3: commissionAt(3),
      },
      cumulative_commission: {
        l1: commissionAt(1),
        l2: commissionAt(1) + commissionAt(2),
        l3: commissionAt(1) + commissionAt(2) + commissionAt(3),
      },
      // The same figures before the add-on rate is layered on. The quote
      // already excludes the add-on from commission, so the two agree; they
      // are separate fields because the deal screen reads both and would
      // throw on the missing one.
      cumulative_commission_without_addon: {
        l1: commissionAt(1),
        l2: commissionAt(1) + commissionAt(2),
        l3: commissionAt(1) + commissionAt(2) + commissionAt(3),
      },
      commission: {
        l1: commissionAt(1),
        l2: commissionAt(2),
        l3: commissionAt(3),
      },
    },
  };
}

/* ------------------------------------------- partners, as a bare array */

export type GetChannelRequestType = Partial<{
  headers: { Authorization: string };
  per_page: number;
}>;

/**
 * The partner list for the orders table's filter.
 *
 * Xano had two endpoints called getChannel returning different shapes; this
 * is the one that returns a bare array. The other, which wraps the rows in
 * `items`, lives in legacy-lookup-api. Keeping both spellings is cheaper than
 * making the screens agree.
 */
export async function getChannel(
  payload: { params?: GetChannelRequestType } = {},
): Promise<{ data: { id: string; name: string; status: string }[] }> {
  const perPage = Math.min(payload.params?.per_page ?? 100, 100);
  const rows: { id: string; name: string; status: string }[] = [];
  for (let page = 1; page <= 20; page += 1) {
    const { data } = await moneefloApi.get("/organizations", {
      params: { type: "partner", page, perPage },
    });
    const items = (data?.items ?? []) as typeof rows;
    rows.push(...items);
    if (items.length < perPage || rows.length >= Number(data?.total ?? 0)) break;
  }
  return { data: rows };
}

/* -------------------------------------------------- creating a deal ------ */

export type PostDealCalcRequestType = Record<string, unknown>;

/**
 * The create-deal screen's own calculation call.
 *
 * It reads exactly the fields postDealCalcWithMargin returns, and the quote
 * endpoint is the same one, so this adds the two partner margins the screen
 * sends and then defers.
 */
export async function postDealCalc(
  payload: Payload<PostDealCalcRequestType>,
): Promise<{ data: any }> {
  // The partner's margin used to be added to our own add-on, which priced
  // it correctly but left it out of what the partner is owed. The backend
  // takes it as the partner's own share now, so it travels as itself.
  return postDealCalcWithMargin(payload);
}

export type GetServicePartnersRequestType = Partial<{
  headers: { Authorization: string };
  purpose_id: number;
  currency: string;
}>;

/**
 * Who can settle this corridor.
 *
 * Xano narrowed by purpose and currency. The settlement rates say exactly
 * who can price a currency, so they are asked first — but reading them means
 * reading our own buying price, which most staff are not allowed to do. For
 * everyone else the list falls back to every service partner, unnarrowed:
 * the quote picks the partner anyway, so this dropdown is an override, and
 * an override that can name a partner who cannot price the corridor is
 * better than one that is empty.
 */
export async function getServicePartners(
  payload: Payload<GetServicePartnersRequestType> & {
    params?: GetServicePartnersRequestType;
  },
): Promise<{ status: number; data: any[] }> {
  const currency = payload.params?.currency ?? payload.data?.currency;
  const purposeId = payload.params?.purpose_id ?? payload.data?.purpose_id;

  // The partners who actually serve this corridor, cheapest first, from the
  // same margins the quote prices off. This used to ask `/settlement-rates`,
  // which margins replaced: that table has been empty since, so the picker
  // was empty for everyone — including the desk allowed to see cost.
  try {
    const { data } = await moneefloApi.get("/pricing/corridor-partners", {
      params: { currency, purposeId },
    });
    return {
      status: 200,
      data: ((data ?? []) as { id: string; name: string }[]).map((partner) => ({
        id: partner.id,
        display_name: partner.name,
      })),
    };
  } catch {
    // Who we buy from and what we pay is not a partner's to see, so there
    // is deliberately no fallback: an empty picker means the server's own
    // choice of the cheapest corridor stands, which is what should happen.
    return { status: 200, data: [] };
  }
}

export type GetExtraFieldsRequestType = Partial<{
  headers: { Authorization: string };
  purpose_id: string;
  service_partner_id: string;
}>;

/**
 * Per-partner extra fields on the deal form.
 *
 * Xano let an operator configure these per purpose and service partner.
 * Nothing models that yet, so the form asks for nothing extra rather than
 * inventing questions.
 */
export async function getExtraFields(
  _payload: Payload<GetExtraFieldsRequestType>,
): Promise<{ status: number; data: { name: string; type: string }[] }> {
  return { status: 200, data: [] };
}

export type GetPartnerL2RequestType = Partial<{
  headers: { Authorization: string };
}>;

/** The caller's own level-2 partner, used to default the margin fields. */
export async function getPartnerL2(
  _payload: Payload<GetPartnerL2RequestType>,
): Promise<{ data: { ID: string | null } }> {
  const { data } = await moneefloApi.get("/auth/me");
  return { data: { ID: data?.organizationId ?? null } };
}

export type PostCreateDealRequestType = Record<string, unknown>;

/**
 * Booking a deal.
 *
 * A Xano deal was a rate held for a customer before an order existed. Here it
 * is the order itself, placed as `quoted`: the rate is locked, nothing has
 * been paid, and confirming it later is a status move rather than a second
 * record.
 *
 * The remitter is looked up by phone before being created, so booking a
 * second deal for the same person does not make a second customer.
 */
export async function postCreateDeal(
  payload: Payload<FormData | PostCreateDealRequestType>,
): Promise<{
  data: { deals: { id: string; orderNo: string }; payment: string };
}> {
  const form = payload.data;
  const read = (key: string): string =>
    (form instanceof FormData
      ? ((form.get(key) as string | null) ?? "")
      : String(
          (form as Record<string, unknown>)?.[key] ?? "",
        )) as string;

  const phone = read("customer_phone").trim();
  const customerId = await findOrCreateRemitter({
    fullName: read("remitter_name").trim(),
    phone,
    email: read("customer_email").trim() || undefined,
  });

  const { data } = await moneefloApi.post("/orders", {
    customerId,
    purposeId: read("purpose_id") || undefined,
    currency: read("currency"),
    fxAmount: Number(read("amount")) || 0,
    fxCharges: read("fx_charges") === "OUR" ? "OUR" : "BEN",
    addOnRate: Number(read("addon_rates")) || undefined,
    // Priced the same way as the estimate: the partner is a link in the
    // chain and earns their own margin, rather than it being folded into
    // ours and never reaching them.
    originOrgId: originOrg(read("created_by")),
    partnerMargin: read("partner_margin") !== ""
      ? Number(read("partner_margin"))
      : undefined,
    discount: Number(read("discount")) || undefined,
    customerRate: Number(read("customer_rate")) || undefined,
    settlementRate: Number(read("settlement_rates")) || undefined,
    // Where the money is going. The order wizard's first step will not
    // pass without it, so dropping it here made every deal unusable.
    country: read("country") || undefined,
    ttCharge: Number(read("tt_charges")) || undefined,
    foreignBankCharge: Number(read("foreign_bank_charges")) || undefined,
    servicePartnerId: read("service_partner_id") || undefined,
    // A deal is a held rate, not a placed order -- unless the desk already
    // has the partner's rate, which is the thing that confirms one.
    initialStatus: read("already_settled") === "true" ? "confirmed" : "quoted",
  });

  const order = data as {
    id?: string;
    orderNo?: string;
    totalPayableMinor?: string;
  };
  return {
    data: {
      // Two identities: the uuid is what the API answers to, the number is
      // what a person reads out over the phone. Both travel together so a
      // screen never has to show one where it meant the other.
      deals: { id: order.id ?? "", orderNo: order.orderNo ?? "" },
      payment: rupees(order.totalPayableMinor).toFixed(2),
    },
  };
}

/** The deal screen sends "-1" for a deal we are doing ourselves. */
function originOrg(createdBy: string): string | undefined {
  const id = createdBy.trim();
  return id && id !== "-1" ? id : undefined;
}

async function findOrCreateRemitter(remitter: {
  fullName: string;
  phone: string;
  email?: string;
}): Promise<string> {
  if (remitter.phone) {
    const { data } = await moneefloApi.get("/customers", {
      params: { search: remitter.phone, perPage: 5 },
    });
    const match = ((data?.items ?? []) as { id: string; phone?: string }[]).find(
      (customer) => customer.phone === remitter.phone,
    );
    if (match) return match.id;
  }
  const { data } = await moneefloApi.post("/customers", {
    fullName: remitter.fullName || "Unnamed remitter",
    phone: remitter.phone || undefined,
    email: remitter.email,
  });
  return (data as { id: string }).id;
}

/* --------------------------------------------------------- the deal lists */

export type GetDealRequestType = Partial<{
  headers: { Authorization: string };
  page: number;
  per_page: number;
  service_partner_id: string | number;
  start: string;
  end: string;
  status: string;
  utilization_status: string;
  q: string;
}>;

/** unconfirmed | confirmed | hold | cancelled | fulfilled, as the lists say. */
const DEAL_STATUS: Record<string, string> = {
  draft: "unconfirmed",
  quoted: "unconfirmed",
  confirmed: "confirmed",
  on_hold: "hold",
  cancelled: "cancelled",
  processing: "fulfilled",
  fulfilled: "fulfilled",
  refunded: "fulfilled",
};

/** Which of our statuses each of those covers, for filtering. */
const ORDERS_FOR_DEAL_STATUS: Record<string, string> = {
  unconfirmed: "draft,quoted",
  confirmed: "confirmed",
  hold: "on_hold",
  cancelled: "cancelled",
  fulfilled: "processing,fulfilled,refunded",
};

/**
 * Deals.
 *
 * A Xano deal was a rate held before an order existed; here the order is the
 * deal, and its status says which. "Utilized" therefore means the order has
 * been worked — anything past confirmed — rather than a separate flag.
 */
export async function getDeal(
  payload: Payload<GetDealRequestType> & { params?: GetDealRequestType },
): Promise<{ data: any }> {
  const query = payload.params ?? payload.data ?? {};
  const page = Number(query.page) > 0 ? Number(query.page) : 1;
  const perPage = Math.min(Number(query.per_page) || 25, 100);

  const utilization = query.utilization_status;
  // Every status a deal can be in. Named explicitly rather than left
  // undefined: the orders endpoint now leaves draft and quoted out when
  // no status is asked for, because an unconfirmed deal is not an order
  // and should not appear in the orders list. The deals screen wants
  // exactly those, so it has to say so.
  const ALL_DEAL_STATUSES =
    "draft,quoted,confirmed,on_hold,processing,fulfilled,refunded,cancelled";

  const status = query.status
    ? ORDERS_FOR_DEAL_STATUS[query.status]
    : utilization === "unutilized"
      ? "draft,quoted,confirmed,on_hold"
      : utilization === "utilized"
        ? "processing,fulfilled,refunded"
        : ALL_DEAL_STATUSES;

  const { data } = await moneefloApi.get("/orders", {
    params: {
      // Both deals screens sit under Remittance, and a Xano deal was a
      // remittance rate booking -- forex never had them. Without this the
      // generic orders show up here as deals.
      category: "remittance",
      page,
      perPage,
      status,
      // Xano's search came through with a ":*" suffix from the screen.
      search: query.q?.replace(/:\*$/, "").trim() || undefined,
      placedFrom: query.start || undefined,
      placedTo: query.end || undefined,
    },
  });

  const items = (data?.items ?? []) as (OrderListRow & {
    purpose: string | null;
    settlementRate: string | null;
    servicePartnerId: string | null;
  })[];
  const total = Number(data?.total ?? items.length);
  const pageTotal = Math.max(1, Math.ceil(total / perPage));

  return {
    data: {
      items: items.map((order) => ({
        id: order.orderNo.replace(/^(ORD-)?MF-/, ""),
        _purpose: { name: order.purpose ?? "" },
        service_partner_rates: order.settlementRate ?? "",
        service_partner_id: order.servicePartnerId,
        _service_partner: {
          id: order.servicePartnerId,
          display_name: order.servicePartner ?? "",
        },
        currency: order.currency ?? "",
        amount: order.fxAmountMinor
          ? String(Number(order.fxAmountMinor) / 100)
          : "",
        amount_inr: Number(order.totalPayableMinor ?? 0) / 100,
        customer_name: order.customer.name,
        status: DEAL_STATUS[order.status] ?? order.status,
        utilization_status: ["processing", "fulfilled", "refunded"].includes(
          order.status,
        )
          ? "utilized"
          : "unutilized",
      })),
      pageTotal,
      curPage: page,
      itemsReceived: items.length,
      itemsTotal: total,
      offset: (page - 1) * perPage,
      perPage,
      nextPage: page < pageTotal ? page + 1 : null,
      prevPage: page > 1 ? page - 1 : null,
    },
  };
}

type OrderListRow = {
  orderNo: string;
  status: string;
  customer: { name: string };
  currency: string | null;
  fxAmountMinor: string | null;
  totalPayableMinor: string | null;
  servicePartner: string | null;
};

export type GetStaffServicePartnerRequestType = Partial<{
  headers: { Authorization: string };
}>;

/** The service-provider filter on the deals list. Same rows as above. */
export async function getStaffServicePartner(
  payload: Payload<GetStaffServicePartnerRequestType> = {},
): Promise<{ data: any[] }> {
  const { data } = await getServicePartners(payload);
  return { data };
}

/* ------------------------------------------------ one deal, in detail ---- */

export type GetDealDetailsRequestType = Partial<{
  deal_id: string | number;
  headers: { Authorization: string };
}>;

/** Micros to a plain rate, which the screen calls .toFixed(4) on. */
const perUnit = (micros: string | null | undefined): number =>
  Number(micros ?? 0) / 1_000_000;

/**
 * The deal detail screen, answered by GET /orders/:id.
 *
 * The screen adds the settlement rate and the three commission rates
 * together to show the customer rate, and calls .toFixed on each, so every
 * one of them has to be a number and none may be missing. Xano returned
 * per-unit rates; the new API returns commission as an amount, because an
 * amount is what gets paid — so the rates come from commissionRateByLevel,
 * which is that amount over the size of the order.
 */
export async function getDealDetails(
  payload: Payload<GetDealDetailsRequestType>,
): Promise<{ data: any }> {
  const dealId = payload.data?.deal_id;
  if (dealId === undefined) throw new Error("deal_id is not provided");

  const { data } = await moneefloApi.get(
    `/orders/${encodeURIComponent(String(dealId))}`,
  );
  const order = data as {
    chain?: { id: string; name: string; level: number }[];
    cancellationCharges?: {
      organizationId: string;
      organization: string;
      partnerLevel: number | null;
      amountMinor: string;
    }[];
    id: string;
    orderNo: string;
    status: string;
    currency: string | null;
    fxRate: string | null;
    fxAmountMinor: string | null;
    purpose: string | null;
    purposeId: string | null;
    purposeMode: string | null;
    fxCharges: string | null;
    placedAt: string | null;
    charges?: { type: string; amountMinor: string; taxMinor: string }[];
    settlementRate?: string | null;
    marketRate?: string | null;
    commissionRateByLevel?: Record<string, string>;
    customer: { id: string; name: string; phone: string | null };
    servicePartnerId?: string | null;
    servicePartnerOrganization?: {
      id: string;
      name: string;
      email: string | null;
      city: string | null;
      state: string | null;
    } | null;
    originOrganization?: {
      id: string;
      name: string;
      email: string | null;
      phone: string | null;
      city: string | null;
      state: string | null;
      partnerLevel: number | null;
    } | null;
    remittance?: { countryName?: string | null } | null;
    createdBy?: { name: string } | null;
  };

  const rate = (level: string): number =>
    Number(order.commissionRateByLevel?.[level] ?? 0);
  const l2 = rate("l2");
  const l3 = rate("l3");
  const customerRate = Number(order.fxRate ?? 0);
  const settlementRate = perUnit(order.settlementRate);
  const sp = order.servicePartnerOrganization;
  const origin = order.originOrganization;
  /** A charge line in rupees, inclusive of its tax, as the wizard shows it. */
  const charge = (type: string): number => {
    const line = order.charges?.find((c) => c.type === type);
    return line ? (Number(line.amountMinor) + Number(line.taxMinor)) / 100 : 0;
  };

  return {
    data: {
      // The number, not the uuid: this is the one the screen prints.
      id: order.orderNo,
      status: DEAL_STATUS[order.status] ?? order.status,
      created_at: order.placedAt ?? "",
      currency: order.currency ?? "",
      country: order.remittance?.countryName ?? "",
      amount: order.fxAmountMinor ? Number(order.fxAmountMinor) / 100 : 0,
      customer_name: order.customer.name,
      customer_phone: order.customer.phone ?? "",
      customer_email: "",
      customer_pan: "",
      purpose_id: order.purposeId ?? "",
      // Which wire service the purpose is for. The beneficiary form differs
      // by it, and choosing on the id means keeping a list of ids in the UI.
      purpose_mode: order.purposeMode ?? "",
      _purpose: {
        id: order.purposeId ?? "",
        name: order.purpose ?? "",
        mode: order.purposeMode ?? "",
      },
      // The order wizard reprices off the deal and needs all three.
      fx_charges: order.fxCharges ?? "BEN",
      tt_charges: charge("tt_charge"),
      foregin_bank_rates: charge("bank_charge"),
      created_by: origin?.id ?? "",
      // What the customer was actually quoted. Adding the settlement rate
      // to the commissions misses any adjustment the operator made, so a
      // deal booked at a agreed rate showed the standard one instead.
      customer_rate: customerRate,
      // Absent for a partner, who may not see what the currency costs us.
      service_partner_rates: settlementRate,
      eforex_rates: perUnit(order.marketRate),
      // L1 earns whatever is left once the partner and the rest of the
      // chain are paid, which is not the same as its configured rate: an
      // operator who moved the price moved L1's share, nobody else's. A
      // deal sold at 60 against a 62 settlement leaves L1 at -2, not the
      // 0.30 the rate card says.
      l1_commission_rates:
        settlementRate > 0
          ? Number((customerRate - settlementRate - l2 - l3).toFixed(4))
          : rate("l1"),
      l2_commission_rates: l2,
      l3_commission_rates: l3,
      // Who is in this chain, for the cancellation form: one charge field
      // per partner actually involved, which cannot be worked out from
      // names alone because the charge is posted back by id.
      chain: order.chain ?? [],
      cancellation_charges: order.cancellationCharges ?? [],
      service_partner_id: order.servicePartnerId ?? null,
      _service_partner: {
        id: sp?.id ?? "",
        display_name: sp?.name ?? "",
        email: sp?.email ?? "",
        legal_addr_city: sp?.city ?? "",
        legal_addr_state: sp?.state ?? "",
      },
      _channels: {
        id: origin?.id ?? "",
        level: origin?.partnerLevel ?? 1,
        name: origin?.name ?? "",
        phone: origin?.phone ?? "",
        email: origin?.email ?? "",
        address_line_1: "",
        address_line_2: "",
        city: origin?.city ?? "",
        state: origin?.state ?? "",
        pincode: "",
      },
      createdByStaff: order.createdBy
        ? { fname: order.createdBy.name, id: "" }
        : null,
      utilization_status: ["processing", "fulfilled", "refunded"].includes(
        order.status,
      )
        ? "utilized"
        : "unutilized",
      // Nothing links a deal to a lead yet, so the screen offers "Link Lead"
      // rather than showing one that is not there.
      leadDetail: null,
      service_partner_cancellation_charge: 0,
      channel_partner_cancellaton_charge: 0,
    },
  };
}

export type PutServiceRateRequestType = Partial<{
  service_partner_rates: number;
  foreign_bank_rate: number;
  tt_charges: number;
  service_partner_id: number;
  deal_id: string | number;
  headers: { Authorization: string };
}>;

/**
 * The rate the desk agreed with the service partner on the phone, which is
 * what confirms a deal.
 *
 * Both the pencil on the deals list and the detail screen end up here. The
 * customer's rate does not move: they have already been quoted, so the
 * difference between the estimate and the agreed rate lands on our margin.
 */
export async function putServiceRate(
  payload: Payload<PutServiceRateRequestType>,
): Promise<{ data: unknown }> {
  const body = payload.data ?? {};
  if (body.deal_id === undefined) {
    throw new Error("deal_id is not provided");
  }
  const { data } = await moneefloApi.post(
    `/orders/${encodeURIComponent(String(body.deal_id))}/settlement-rate`,
    {
      settlementRate: Number(body.service_partner_rates),
      // A deal usually has no partner yet -- you ring round before you know
      // the rate -- and the screens hand back null or "" for that. Passing
      // them on became BigInt("null") and answered "Unknown service
      // partner" on the ordinary case.
      servicePartnerId:
        body.service_partner_id === undefined ||
        body.service_partner_id === null ||
        String(body.service_partner_id).trim() === "" ||
        String(body.service_partner_id) === "null"
          ? undefined
          : String(body.service_partner_id),
    },
  );
  return { data };
}

/* ------------------------------------------------ a quotation as a PDF -- */

export type QuoteFromHomePageRequestType = Partial<{
  headers: { Authorization: string };
  contacts_id: string;
  Name: string;
  email: string;
  number: string;
  pancard: string;
  data: Record<string, unknown>;
  purpose_id: string;
  channel: string[];
  country: string;
  amount: string;
  document: unknown[];
  product_id: string;
  /** Added for the new backend, which prices the quotation itself. */
  quote: {
    purpose_id?: string;
    currency?: string;
    fx_amount?: string | number;
    fx_charges?: string;
    customer_rate?: string | number;
    settlement_rate?: string | number;
    service_partner_id?: string | number;
  };
}>;

/**
 * Whether the operator ticked "email" on the share step.
 *
 * WhatsApp is the other box and has no transport, so ticking only that one
 * sends nothing -- `quoteFromHomePage` reports back what actually happened
 * rather than letting the screen claim it went.
 */
function wantsEmail(channel: unknown): boolean {
  return Array.isArray(channel) && channel.includes("email");
}

/** What the screens get back: the url, plus whether the email went. */
export type QuotationReply = {
  status: number;
  data: string;
  emailed: { sent: boolean; reason?: string } | null;
};

/** What both quotation endpoints answer with. */
type QuotationResult = {
  url: string;
  emailed?: { sent: boolean; reason?: string } | null;
};

/** The contact the modal matched, if it matched one. */
function contactId(value: unknown): string | undefined {
  const id = value == null ? "" : String(value).trim();
  return id.length > 0 ? id : undefined;
}

/**
 * The quotation the dashboard hands to a customer.
 *
 * The PDF is priced server-side from the same inputs the panel used rather
 * than from the figures the screen happens to be holding, so the document
 * and the screen cannot drift apart. Comes back as a short-lived link,
 * which is what the caller downloads.
 */
export async function quoteFromHomePage(
  payload: Payload<QuoteFromHomePageRequestType>,
): Promise<QuotationReply> {
  const body = payload.data ?? {};
  const q = body.quote ?? {};

  // The counter's basket goes to its own endpoint: several products rather
  // than one corridor, and no service partner in the price.
  if (body.product_id) {
    return quoteForexFromHomePage(body);
  }
  if (!q.purpose_id || !q.currency || !q.fx_amount) {
    throw new Error("A purpose, a currency and an amount are needed to quote.");
  }

  const { data } = await moneefloApi.post("/quotations/pdf", {
    purposeId: String(q.purpose_id),
    currency: q.currency,
    fxAmount: Number(q.fx_amount),
    fxCharges: q.fx_charges === "OUR" ? "OUR" : "BEN",
    customerRate: q.customer_rate ? Number(q.customer_rate) : undefined,
    settlementRate: q.settlement_rate ? Number(q.settlement_rate) : undefined,
    servicePartnerId: q.service_partner_id
      ? String(q.service_partner_id)
      : undefined,
    customerName: body.Name || undefined,
    customerEmail: body.email || undefined,
    customerPhone: body.number || undefined,
    country: body.country || undefined,
    // Quoting somebody starts a deal, so the desk expects a lead waiting
    // afterwards. The contact box may have matched nobody, in which case
    // the typed details become one.
    leadCustomerId: contactId(body.contacts_id),
    openLead: true,
    emailQuote: wantsEmail(body.channel),
  });

  // The screen downloads whatever comes back, so it is the url itself.
  const result = data as QuotationResult;
  return { status: 200, data: result.url, emailed: result.emailed ?? null };
}

/* ------------------------------------------ the counter's quotation */

type ForexQuoteLine = {
  product_id?: number;
  product_name?: string;
  quantity?: number;
  product_rate?: number;
  currency_have?: string;
  currency_want?: string;
};

/** Xano's forex product ids; 16 is the travel card, 9 the notes. */
const FOREX_CARD_PRODUCT_ID = 16;

/**
 * A forex quotation, priced and drawn by the new backend.
 *
 * Only the inputs go over: the products, what was typed for each rate, and
 * the service charge. Everything the screen had already worked out is left
 * behind so the document cannot quietly disagree with it — a line with no
 * rate is priced off the live feed and the held spread instead.
 */
async function quoteForexFromHomePage(
  body: NonNullable<QuoteFromHomePageRequestType["data"]>,
): Promise<QuotationReply> {
  // The basket sits one level in, under `data`; the contact details the
  // modal collected sit alongside it at the top.
  const quote = (body.data ?? {}) as Record<string, unknown>;
  const lines = (quote.products ?? []) as ForexQuoteLine[];
  if (lines.length === 0) {
    throw new Error("Add at least one product before quoting.");
  }

  // "encashment" is the customer selling notes back to us; anything else is
  // them buying. The foreign currency is whichever side of the swap is not
  // rupees.
  const direction = quote.product_type === "encashment" ? "sell" : "buy";

  const products = lines.map((line) => {
    const currency =
      (line.currency_have !== "INR" ? line.currency_have : line.currency_want) ??
      "";
    if (!currency || currency === "INR") {
      throw new Error("Every product needs a currency before quoting.");
    }
    return {
      currency,
      fxProduct:
        Number(line.product_id) === FOREX_CARD_PRODUCT_ID ? "card" : "currency",
      productName: line.product_name || undefined,
      quantity: Number(line.quantity) || 0,
      // Left out when the operator did not overrule it, which is the signal
      // to price the line off the feed.
      rate: Number(line.product_rate) > 0 ? Number(line.product_rate) : undefined,
    };
  });

  const { data } = await moneefloApi.post("/quotations/forex-pdf", {
    direction,
    products,
    serviceCharge: Number(quote.overall_service_charge) || 0,
    serviceChargeIncludesGst: Boolean(quote.gst_included),
    // What was typed into the modal wins over the matched contact: the
    // operator may have corrected it, and for a number with no contact
    // behind it the typed name is all there is.
    customerName:
      (body.Name as string) || (quote.customer_name as string) || undefined,
    customerEmail:
      (body.email as string) || (quote.customer_email as string) || undefined,
    customerPhone:
      (body.number as string) ||
      (quote.customer_phone_number as string) ||
      undefined,
    customerId: contactId(body.contacts_id),
    openLead: true,
    emailQuote: wantsEmail(body.channel),
  });

  const result = data as QuotationResult;
  return { status: 200, data: result.url, emailed: result.emailed ?? null };
}

/* ------------------------------------------------------- cancelling a deal */

export type ChainLink = { id: string; name: string; level: number };

export type CancelDealRequestType = Partial<{
  headers: { Authorization: string };
  deal_id: string | number;
  reason: string;
  /** One per partner in the chain, in rupees as the form collects them. */
  charges: { organizationId: string; amountMinor: number }[];
}>;

/**
 * Cancels a deal.
 *
 * Xano had two calls and two fixed fields — a "service partner" charge
 * and a "channel partner" one — and the screen called them from the
 * unmigrated module, so the button did nothing at all: the request was
 * rejected before it was sent and the handler toasted `undefined`.
 *
 * One call now, and the charges are a list rather than two columns,
 * because a chain is MoneeFlo alone or MoneeFlo plus one or two partners
 * beneath. The server refuses a charge against an organization that was
 * not in this deal, and refuses any charge at all on a deal that was
 * never confirmed.
 */
export async function cancelDeal(
  payload: Payload<CancelDealRequestType>,
): Promise<{ status: number; data: unknown }> {
  const dealId = payload.data?.deal_id;
  if (dealId === undefined) throw new Error("deal_id is not provided");

  const charges = (payload.data?.charges ?? []).filter(
    (charge) => Number.isFinite(charge.amountMinor) && charge.amountMinor > 0,
  );

  const { data } = await moneefloApi.post(
    `/orders/${encodeURIComponent(String(dealId))}/cancel`,
    {
      reason: payload.data?.reason,
      ...(charges.length > 0 ? { charges } : {}),
    },
  );
  return { status: 200, data };
}
